import * as Api from '@ViewModels';
import { ImpersonationBroker } from '@ViewModels';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const COMPANY_FILTER_KEY = 'companyFilter';
const DEFAULT_PAGE_SIZE = 25;

/**
 * Hook for fetching filtered and sorted companies
 */
export function useCompaniesQuery({
	searchRequest,
	sortDescriptor,
	pageSize = DEFAULT_PAGE_SIZE,
	impersonationContext,
	enabled = true,
}: {
	searchRequest?: Api.CompaniesFilterRequest;
	sortDescriptor?: Api.ISortDescriptor;
	pageSize?: number;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();

	return useInfiniteQuery<Api.IPagedCollection<Api.ICompany>>({
		queryKey: [COMPANY_FILTER_KEY, searchRequest, sortDescriptor, pageSize, impersonationContext],
		queryFn: async ({ pageParam }) => {
			const params = {
				...(sortDescriptor || {}),
				pageSize,
				pageToken: pageParam,
				expand: 'TagsCollection',
			};

			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ICompany>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: 'company/filter',
					queryParams: params,
					impersonationContext,
				}),
				'POST',
				searchRequest
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		enabled,
		initialPageParam: '',
	});
}

/**
 * Hook for deleting companies
 */
export function useDeleteCompaniesMutation({
	onSuccess,
	onError,
	impersonationContext,
}: {
	onSuccess?: (data: Api.IBulkOperationResult<string>) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (companyIds: string[]) => {
			return userSession.webServiceHelper.callAsync<Api.IBulkOperationResult<string>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: 'company',
					impersonationContext,
				}),
				'DELETE',
				companyIds
			);
		},
		onSuccess: data => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_FILTER_KEY] });
			if (onSuccess) {
				onSuccess(data);
			}
		},
		onError,
	});
}

/**
 * Hook for merging companies
 */
export function useMergeCompaniesMutation({
	onSuccess,
	onError,
	impersonationContext,
}: {
	onSuccess?: (data: Api.IOperationResult<Api.ICompany>) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ targetId, companyIds }: { targetId: string; companyIds: string[] }) => {
			return userSession.webServiceHelper.callAsync<Api.IOperationResult<Api.ICompany>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `company/${targetId}/merge`,
					impersonationContext,
				}),
				'POST',
				companyIds
			);
		},
		onSuccess: data => {
			queryClient.invalidateQueries({ queryKey: [COMPANY_FILTER_KEY] });
			if (onSuccess) {
				onSuccess(data);
			}
		},
		onError,
	});
}

export function invalidateAllCompanyFilterQueries() {
	reactQueryClient.invalidateQueries({ queryKey: [COMPANY_FILTER_KEY] });
}

export function getCompaniesQueryKey(
	searchRequest?: Api.CompaniesFilterRequest,
	sortDescriptor?: Api.ISortDescriptor,
	pageSize: number = DEFAULT_PAGE_SIZE,
	impersonationContext?: Api.IImpersonationContext
) {
	return [COMPANY_FILTER_KEY, searchRequest, sortDescriptor, pageSize, impersonationContext];
}
